import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { User, Mail, Lock, Building, Phone, MapPin, Users, Calendar, DollarSign, ArrowRight, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import './SignupPage.css';

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup, user, loading } = useAuth();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get current year for dynamic revenue labels
  const currentYear = new Date().getFullYear();
  const previousYear1 = currentYear - 1;
  const previousYear2 = currentYear - 2;

  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    
    // Step 2: Business Information
    businessType: '',
    businessName: '',
    employeeCount: '',
    yearsInBusiness: '',
    corporationType: '',
    
    // Step 3: Location & Demographics
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    language: 'en',
    age: '',
    ethnicity: '',
    gender: '',
    
    // Step 4: Financial Information
    [`annualRevenue${previousYear2}`]: '',
    [`annualRevenue${previousYear1}`]: '',
    [`annualRevenue${currentYear}`]: '',
    
    // Terms acceptance
    termsAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // Check if user is already logged in and redirect to fullchat
  useEffect(() => {
    if (!loading && user) {
      console.log('User already logged in, redirecting to fullchat...');
      navigate('/fullchat', { replace: true });
    }
  }, [user, loading, navigate]);

  const businessTypes = [
    'Beauty Salon',
    'Restaurant',
    'Retail Store',
    'Coffee Shop',
    'Barbershop',
    'Bakery',
    'Gym',
    'Auto Repair',
    'Laundry Service',
    'Pet Grooming',
    'Tattoo Parlor',
    'Nail Salon',
    'Photography Studio',
    'Consulting',
    'Daycare Center',
    'Cleaning Service',
    'Food Truck',
    'Yoga Studio',
    'Music School',
    'Dance Studio',
    'Art Gallery',
    'Bookstore',
    'Florist Shop',
    'Jewelry Store',
    'Vintage Shop',
    'Tech Startup',
    'Dental Office',
    'Law Firm',
    'Real Estate',
    'Insurance Agency',
    'Accounting Firm',
    'Marketing Agency',
    'Web Design',
    'Construction',
    'Landscaping',
    'Plumbing',
    'Electrical',
    'HVAC',
    'Roofing',
    'Painting',
    'Carpentry',
    'Other'
  ];

  const employeeCounts = [
    '1-5 employees',
    '6-10 employees',
    '11-25 employees',
    '26-50 employees',
    '51-100 employees',
    '100+ employees'
  ];

  const yearsInBusinessOptions = [
    'Less than 1 year',
    '1-2 years',
    '3-5 years',
    '6-10 years',
    '11-20 years',
    '20+ years'
  ];

  const corporationTypes = [
    'Sole Proprietorship',
    'Partnership',
    'LLC',
    'Corporation (C-Corp)',
    'S-Corporation',
    'Non-profit',
    'Other'
  ];

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana',
    'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
    'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska',
    'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina',
    'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
    'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const ageRanges = [
    '18-24',
    '25-34',
    '35-44',
    '45-54',
    '55-64',
    '65+'
  ];

  const ethnicities = [
    'Black or African American',
    'Hispanic or Latino',
    'Asian',
    'Native American or Alaska Native',
    'Native Hawaiian or Pacific Islander',
    'Middle Eastern or North African',
    'South Asian',
    'East Asian',
    'Southeast Asian',
    'Central Asian',
    'Caribbean',
    'African',
    'Latin American',
    'Indigenous Australian',
    'Indigenous Canadian',
    'Indigenous Mexican',
    'Indigenous Central American',
    'Indigenous South American',
    'Mixed Race or Multiracial',
    'Biracial',
    'Triracial',
    'Other',
    'Prefer not to say',
    'White'
  ];

  const genders = [
    'Male',
    'Female',
    'Other',
    'Prefer not to say'
  ];

  const totalSteps = 4;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone field - only send if it has a value
    let processedValue = value;
    if (name === 'phone' && !value.trim()) {
      processedValue = ''; // Empty string for optional phone
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear success message when user makes changes
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.firstName.trim()) newErrors.firstName = t('validation.firstNameRequired');
        if (!formData.lastName.trim()) newErrors.lastName = t('validation.lastNameRequired');
        if (!formData.email.trim()) newErrors.email = t('validation.emailRequired');
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('validation.email');
        if (!formData.password) newErrors.password = t('validation.passwordRequired');
        else if (formData.password.length < 8) newErrors.password = t('validation.password');
        if (!formData.confirmPassword) newErrors.confirmPassword = t('validation.confirmPasswordRequired');
        else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = t('validation.passwordMatch');
        }
        // Phone validation - optional but if provided, must be at least 7 characters
        if (formData.phone && formData.phone.trim() && formData.phone.trim().length < 7) {
          newErrors.phone = t('validation.phoneMinLength');
        }
        break;
      
      case 2:
        if (!formData.businessType) newErrors.businessType = t('validation.businessTypeRequired');
        if (!formData.businessName.trim()) newErrors.businessName = t('validation.businessNameRequired');
        if (!formData.employeeCount) newErrors.employeeCount = t('validation.employeeCountRequired');
        if (!formData.yearsInBusiness) newErrors.yearsInBusiness = t('validation.yearsInBusinessRequired');
        break;
      
      case 3:
        // Address fields are now optional
        break;
      
      case 4:
        // Financial information is optional
        if (!formData.termsAccepted) {
          newErrors.termsAccepted = t('validation.termsAcceptedRequired');
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      // Clear success message when moving to next step
      setSuccessMessage('');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Clear success message when moving to previous step
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all steps before submission
    let allStepsValid = true;
    for (let step = 1; step <= totalSteps; step++) {
      if (!validateStep(step)) {
        allStepsValid = false;
        break;
      }
    }
    
    if (!allStepsValid) {
      setErrors({ submit: t('auth.signupCompleteAllFields') });
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (signup && typeof signup === 'function') {
        // Prepare data for submission - remove empty phone if not provided
        const submitData = { ...formData };
        if (!submitData.phone || submitData.phone.trim() === '') {
          delete submitData.phone; // Don't send empty phone to backend
        }
        
        // Remove termsAccepted from submission data (not needed by backend)
        delete submitData.termsAccepted;
        
        // Final validation check before submission
        const requiredFields = ['email', 'password', 'firstName', 'lastName', 'businessType', 'businessName'];
        const missingFields = requiredFields.filter(field => !submitData[field] || submitData[field].trim() === '');
        
        if (missingFields.length > 0) {
          setErrors({ submit: t('auth.signupMissingFields').replace('{{fields}}', missingFields.join(', ')) });
          setIsSubmitting(false);
          return;
        }
        
        console.log('🔍 SignupPage - Submitting form data:', submitData);
        console.log('🔍 SignupPage - Required fields check:');
        console.log('  - email:', !!submitData.email);
        console.log('  - password:', !!submitData.password);
        console.log('  - firstName:', !!submitData.firstName);
        console.log('  - lastName:', !!submitData.lastName);
        console.log('  - businessType:', !!submitData.businessType);
        console.log('  - businessName:', !!submitData.businessName);
        const result = await signup(submitData);
        
        if (result.success) {
          if (result.needsLogin) {
            // Auto-login failed, show message and redirect to login
            setSuccessMessage(result.message);
            setErrors({});
            setTimeout(() => {
              navigate('/login');
            }, 3000);
          } else {
            // Auto-login successful, redirect to fullchat
            setSuccessMessage(t('auth.signupSuccessRedirect'));
            setErrors({});
            setTimeout(() => {
              navigate('/fullchat');
            }, 1500);
          }
        } else {
          // Handle backend validation errors
          if (result.errors && Array.isArray(result.errors)) {
            const errorMessages = result.errors.map(err => err.msg).join(', ');
            setErrors({ submit: `${t('auth.validationFailed')}: ${errorMessages}` });
          } else {
            setErrors({ submit: result.error || t('auth.signupFailed') });
          }
          setSuccessMessage('');
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({ submit: t('auth.signupFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="signup-step">
      <div className="step-header">
        <h2>{t('auth.basicInfo')}</h2>
        <p>{t('auth.personalDetails')}</p>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">
            <User size={16} />
            {t('auth.firstName')}
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder={t('auth.firstName')}
            className={errors.firstName ? 'error' : ''}
          />
          {errors.firstName && <span className="error-text">{errors.firstName}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="lastName">
            <User size={16} />
            {t('auth.lastName')}
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder={t('auth.lastName')}
            className={errors.lastName ? 'error' : ''}
          />
          {errors.lastName && <span className="error-text">{errors.lastName}</span>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="email">
          <Mail size={16} />
          {t('auth.email')}
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="your@email.com"
          className={errors.email ? 'error' : ''}
        />
        {errors.email && <span className="error-text">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="phone">
          <Phone size={16} />
          {t('auth.phone')} ({t('common.optional')})
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="5551234567"
          pattern="[0-9]{10,15}"
          title={t('validation.phone')}
          className={errors.phone ? 'error' : ''}
        />
        <small className="form-help">{t('auth.phoneHelp')}</small>
        {errors.phone && <span className="error-text">{errors.phone}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="password">
            <Lock size={16} />
            {t('auth.password')}
          </label>
          <div className="password-input">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('auth.passwordPlaceholder')}
              className={errors.password ? 'error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <span className="error-text">{errors.password}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="confirmPassword">
            <Lock size={16} />
            {t('auth.confirmPassword')}
          </label>
          <div className="password-input">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder={t('auth.confirmPasswordPlaceholder')}
              className={errors.confirmPassword ? 'error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="signup-step">
      <div className="step-header">
        <h2>{t('auth.businessDetails')}</h2>
        <p>{t('auth.businessInfo')}</p>
      </div>
      
      <div className="form-group">
        <label htmlFor="businessName">
          <Building size={16} />
          {t('business.businessName')}
        </label>
        <input
          type="text"
          id="businessName"
          name="businessName"
          value={formData.businessName}
          onChange={handleInputChange}
          placeholder={t('business.businessName')}
          className={errors.businessName ? 'error' : ''}
        />
        {errors.businessName && <span className="error-text">{errors.businessName}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="businessType">
          <Building size={16} />
          {t('business.businessType')}
        </label>
        <select
          id="businessType"
          name="businessType"
          value={formData.businessType}
          onChange={handleInputChange}
          className={errors.businessType ? 'error' : ''}
        >
          <option value="">{t('business.selectBusinessType')}</option>
          {businessTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        {errors.businessType && <span className="error-text">{errors.businessType}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="employeeCount">
            <Users size={16} />
            {t('business.employeeCount')}
          </label>
          <select
            id="employeeCount"
            name="employeeCount"
            value={formData.employeeCount}
            onChange={handleInputChange}
            className={errors.employeeCount ? 'error' : ''}
          >
            <option value="">{t('auth.selectEmployeeCount')}</option>
            {employeeCounts.map(count => (
              <option key={count} value={count}>{count}</option>
            ))}
          </select>
          {errors.employeeCount && <span className="error-text">{errors.employeeCount}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="yearsInBusiness">
            <Calendar size={16} />
            {t('business.yearsInBusiness')}
          </label>
          <select
            id="yearsInBusiness"
            name="yearsInBusiness"
            value={formData.yearsInBusiness}
            onChange={handleInputChange}
            className={errors.yearsInBusiness ? 'error' : ''}
          >
            <option value="">{t('auth.selectYears')}</option>
            {yearsInBusinessOptions.map(years => (
              <option key={years} value={years}>{years}</option>
            ))}
          </select>
          {errors.yearsInBusiness && <span className="error-text">{errors.yearsInBusiness}</span>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="corporationType">
          <Building size={16} />
          {t('business.corporationType')}
        </label>
        <select
          id="corporationType"
          name="corporationType"
          value={formData.corporationType}
          onChange={handleInputChange}
          className={errors.corporationType ? 'error' : ''}
        >
          <option value="">{t('auth.selectType')}</option>
          {corporationTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="signup-step">
      <div className="step-header">
        <h2>{t('auth.address')} & {t('auth.demographics')}</h2>
        <p>{t('auth.additionalInfo')}</p>
      </div>
      
      <div className="form-group">
        <label htmlFor="addressLine1">
          <MapPin size={16} />
          {t('auth.businessLocation')} ({t('common.optional')})
        </label>
        <input
          type="text"
          id="addressLine1"
          name="addressLine1"
          value={formData.addressLine1}
          onChange={handleInputChange}
          placeholder={t('auth.addressLine1')}
          className={errors.addressLine1 ? 'error' : ''}
        />
        {errors.addressLine1 && <span className="error-text">{errors.addressLine1}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="city">
            <MapPin size={16} />
            {t('auth.city')} ({t('common.optional')})
          </label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            placeholder={t('auth.city')}
            className={errors.city ? 'error' : ''}
          />
          {errors.city && <span className="error-text">{errors.city}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="state">
            <MapPin size={16} />
            {t('auth.state')} ({t('common.optional')})
          </label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            className={errors.state ? 'error' : ''}
          >
            <option value="">{t('auth.selectState')}</option>
            {states.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
          {errors.state && <span className="error-text">{errors.state}</span>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="zipCode">
          <MapPin size={16} />
          {t('auth.zipCode')} ({t('common.optional')})
        </label>
        <input
          type="text"
          id="zipCode"
          name="zipCode"
          value={formData.zipCode}
          onChange={handleInputChange}
          placeholder={t('auth.zipCode')}
          className={errors.zipCode ? 'error' : ''}
        />
        {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="age">
            <User size={16} />
            {t('auth.ageRange')}
          </label>
          <select
            id="age"
            name="age"
            value={formData.age}
            onChange={handleInputChange}
          >
            <option value="">{t('auth.selectAgeRange')}</option>
            {ageRanges.map(age => (
              <option key={age} value={age}>{age}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="gender">
            <User size={16} />
            {t('auth.gender')}
          </label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
          >
            <option value="">{t('auth.selectGender')}</option>
            {genders.map(gender => (
              <option key={gender} value={gender}>{gender}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="ethnicity">
          <User size={16} />
          {t('auth.ethnicity')}
        </label>
        <select
          id="ethnicity"
          name="ethnicity"
          value={formData.ethnicity}
          onChange={handleInputChange}
        >
          <option value="">{t('auth.selectEthnicity')}</option>
          {ethnicities.map(ethnicity => (
            <option key={ethnicity} value={ethnicity}>{ethnicity}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="signup-step">
      <div className="step-header">
        <h2>{t('auth.financialInfo')}</h2>
        <p>{t('auth.financialInfoHelp')} ({t('common.optional')})</p>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`annualRevenue${previousYear2}`}>
            <DollarSign size={16} />
            {previousYear2} {t('auth.revenue')}
          </label>
          <select
            id={`annualRevenue${previousYear2}`}
            name={`annualRevenue${previousYear2}`}
            value={formData[`annualRevenue${previousYear2}`]}
            onChange={handleInputChange}
          >
            <option value="">{t('auth.selectRange')}</option>
            <option value="Under $50,000">{t('auth.revenue0-100k')}</option>
            <option value="$50,000 - $100,000">{t('auth.revenue0-100k')}</option>
            <option value="$100,000 - $250,000">{t('auth.revenue100k-500k')}</option>
            <option value="$250,000 - $500,000">{t('auth.revenue100k-500k')}</option>
            <option value="$500,000 - $1,000,000">{t('auth.revenue500k-1m')}</option>
            <option value="Over $1,000,000">{t('auth.revenue10m+')}</option>
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor={`annualRevenue${previousYear1}`}>
            <DollarSign size={16} />
            {previousYear1} {t('auth.revenue')}
          </label>
          <select
            id={`annualRevenue${previousYear1}`}
            name={`annualRevenue${previousYear1}`}
            value={formData[`annualRevenue${previousYear1}`]}
            onChange={handleInputChange}
          >
            <option value="">{t('auth.selectRange')}</option>
            <option value="Under $50,000">{t('auth.revenue0-100k')}</option>
            <option value="$50,000 - $100,000">{t('auth.revenue0-100k')}</option>
            <option value="$100,000 - $250,000">{t('auth.revenue100k-500k')}</option>
            <option value="$250,000 - $500,000">{t('auth.revenue100k-500k')}</option>
            <option value="$500,000 - $1,000,000">{t('auth.revenue500k-1m')}</option>
            <option value="Over $1,000,000">{t('auth.revenue10m+')}</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor={`annualRevenue${currentYear}`}>
          <DollarSign size={16} />
          {currentYear} {t('auth.revenueProjected')}
        </label>
        <select
          id={`annualRevenue${currentYear}`}
          name={`annualRevenue${currentYear}`}
          value={formData[`annualRevenue${currentYear}`]}
          onChange={handleInputChange}
        >
          <option value="">{t('auth.selectRange')}</option>
          <option value="Under $50,000">{t('auth.revenue0-100k')}</option>
          <option value="$50,000 - $100,000">{t('auth.revenue0-100k')}</option>
          <option value="$100,000 - $250,000">{t('auth.revenue100k-500k')}</option>
          <option value="$250,000 - $500,000">{t('auth.revenue100k-500k')}</option>
          <option value="$500,000 - $1,000,000">{t('auth.revenue500k-1m')}</option>
          <option value="Over $1,000,000">{t('auth.revenue10m+')}</option>
        </select>
      </div>

      <div className="terms-section">
        <label className="checkbox-label">
          <input 
            type="checkbox" 
            name="termsAccepted" 
            checked={formData.termsAccepted}
            onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
            required 
          />
          <span className="checkmark"></span>
          {t('auth.agreeToTerms')} <a href="/terms" target="_blank" rel="noopener noreferrer">{t('footer.terms')}</a> {t('common.and')}{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('footer.privacy')}</a>
        </label>
        {errors.termsAccepted && <span className="error-text">{errors.termsAccepted}</span>}
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return renderStep1();
    }
  };

  return (
    <div className="signup-main-container">
      {/* Left side - Welcome section */}
      <div className="signup-welcome-section">
        <div className="signup-logo-section">
          <div
            className="signup-logo-text"
            onClick={() => navigate('/')}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/');
              }
            }}
            title="Go to main page"
            style={{ cursor: 'pointer' }}
          >
            Oakland AI
          </div>
        </div>
        
        <div className="signup-welcome-content">
          <h1 className="signup-main-heading">
            {t('auth.signupWelcomeTitle1')}<br />
            {t('auth.signupWelcomeTitle2')}<br />
            {t('auth.signupWelcomeTitle3')}
          </h1>
          <p className="signup-sub-heading">
            {t('auth.signupWelcomeSub1')}<br />
            {t('auth.signupWelcomeSub2')}
          </p>
          
          <div className="signup-progress-container">
            <div className="signup-progress-bar">
              <div 
                className="signup-progress-fill" 
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
            <p className="signup-progress-text">
              {t('auth.signupProgressText').replace('{{current}}', String(currentStep)).replace('{{total}}', String(totalSteps))}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form section */}
      <div className="signup-form-section">
        <div className="signup-form-container">
          <div className="signup-form-content">
            {/* Form Header */}
            <div className="signup-form-header">
              <h2>{t('auth.createAccount')}</h2>
              <p>{t('auth.joinBusinesses')}</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="signup-form" noValidate>
              {renderCurrentStep()}

              {/* Navigation Buttons - keys ensure stable DOM for React reconciliation (fixes insertBefore when language changes) */}
              <div className="signup-actions">
                {currentStep > 1 ? (
                  <button
                    key="prev"
                    type="button"
                    onClick={prevStep}
                    className="btn-secondary"
                  >
                    <ArrowLeft size={16} />
                    {t('common.previous')}
                  </button>
                ) : null}
                {currentStep < totalSteps ? (
                  <button
                    key="next"
                    type="button"
                    onClick={nextStep}
                    className="btn-primary"
                  >
                    {t('common.next')}
                    <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    key="submit"
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    <span className="signup-submit-content">
                      <span className="signup-submit-text">
                        {isSubmitting ? t('auth.creatingAccount') : t('auth.createAccount')}
                      </span>
                      {isSubmitting ? (
                        <span className="spinner" aria-hidden />
                      ) : (
                        <span className="signup-submit-icon"><ArrowRight size={16} /></span>
                      )}
                    </span>
                  </button>
                )}
              </div>

              {errors.submit && (
                <div className="error-message">{errors.submit}</div>
              )}
              
              {successMessage && (
                <div className="success-message">{successMessage}</div>
              )}
            </form>

            <div className="login-link">
              {t('auth.alreadyHaveAccount')} <a href="/login">{t('auth.signIn')}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;